import { useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { Ring } from '@/components/ui/Chart';
import { CountUp } from '@/components/ui/CountUp';
import { adminApi, type AiAgentEntry, type AiAgentMetrics, type AiBusinessReport, type AiCallRef, type AiReportItem, type CampaignLiveMetrics } from '@/shared/api/adminApi';
import './insights.css';

/**
 * תצוגות שלושת דוחות ה-AI של מסך התובנות. 07/09/2026: שכבת עיצוב חדשה
 * (insights.css) — כרטיסים אחידים, היררכיה טיפוגרפית, גרפים קומפקטיים,
 * צ'יפים רכים ורשימת Action Items מובנית. הנתונים, הסקציות והקישורים
 * (drill-down ליומן השיחות: /admin/cdr?search=<phone>) לא השתנו.
 */

type Tone = 'blue' | 'green' | 'orange' | 'red' | 'purple';

const TONE_COLOR: Record<Tone, string> = {
  blue: 'var(--ai-primary)',
  green: 'var(--ai-ok)',
  orange: 'var(--ai-warn)',
  red: 'var(--ai-danger)',
  purple: 'var(--ai-primary-ink)',
};

// סנטימנט: אותו צבע בכל מקום במסך (חיובי=ירוק, ניטרלי=כחול, שלילי=אדום).
const SENTIMENT_LABEL: Record<string, string> = { positive: 'חיובי', neutral: 'ניטרלי', negative: 'שלילי' };
const SENTIMENT_COLOR: Record<string, string> = { positive: 'var(--ai-ok)', neutral: 'var(--ai-primary)', negative: 'var(--ai-danger)' };
const SENTIMENT_ORDER = ['positive', 'neutral', 'negative'];
function sentimentLabel(k: string): string { return SENTIMENT_LABEL[k] ?? k; }
function sentimentColor(k: string, i: number): string { return SENTIMENT_COLOR[k] ?? ['var(--ai-primary)', 'var(--ai-ok)', 'var(--ai-warn)'][i % 3]; }

/** כותרת סקציה: נקודה סמנטית + כותרת (בלי קו תחתון — ריווח במקום גבול). */
export function SectionHeading({ title, tone, note }: { title: string; tone: Tone; note?: string }) {
  return (
    <div className="ai-sec-head">
      <span className={`ai-dot ai-dot--${tone}`} style={{ background: TONE_COLOR[tone] }} />
      <h3>{title}</h3>
      {note && <span className="ai-sec-note">{note}</span>}
    </div>
  );
}

const CARD_VARIANT: Partial<Record<Tone, string>> = { green: 'ai-card--preserve', orange: 'ai-card--improve', red: 'ai-card--danger' };

export function Section({ title, tone, children, variant, note }: { title: string; tone: Tone; children: React.ReactNode; variant?: 'summary' | 'result'; note?: string }) {
  const cls = ['card', 'ai-card', variant ? `ai-card--${variant}` : CARD_VARIANT[tone] ?? ''].filter(Boolean).join(' ');
  return (
    <section className={cls}>
      <SectionHeading title={title} tone={tone} note={note} />
      {children}
    </section>
  );
}

export function PhoneRefs({ refs }: { refs?: AiCallRef[] }) {
  const navigate = useNavigate();
  const phones = [...new Set((refs ?? []).map((r) => r.phone).filter(Boolean))].slice(0, 6);
  if (phones.length === 0) return null;
  return (
    <div className="ai-links">
      {phones.map((p) => (
        <button
          key={p}
          type="button"
          className="ai-phone"
          dir="ltr"
          title="כל השיחות של המספר"
          onClick={() => navigate(`/admin/cdr?search=${encodeURIComponent(p)}`)}
        >
          {p}
        </button>
      ))}
    </div>
  );
}

export function ItemRow({ item, titleKey = 'title', tone = 'orange' }: { item: AiReportItem; titleKey?: 'title' | 'label' | 'name' | 'question'; tone?: Tone }) {
  const title = item[titleKey] ?? item.title ?? item.label ?? item.name ?? item.question ?? '';
  const hasPct = item.pct != null && item.pct > 0;
  const desc = item.details ?? item.insight;
  return (
    <div className="ai-item">
      <div className="ai-item-head">
        <div className="ai-item-title">
          {item.area && <span className="ai-area">{item.area} — </span>}{title}
        </div>
        {hasPct && <span className={`ai-pct ai-pct--${tone}`}>{item.pct}% מהשיחות</span>}
      </div>
      {/* פס יחסי — מדרג ויזואלית את הפריט הנפוץ ביותר במבט חטוף. */}
      {hasPct && (
        <div className="ai-progress" aria-hidden="true">
          <div style={{ width: `${Math.min(100, item.pct!)}%`, background: TONE_COLOR[tone] }} />
        </div>
      )}
      {desc && <div className="ai-item-desc">{desc}</div>}
      <PhoneRefs refs={item.call_refs} />
    </div>
  );
}

function ItemsSection({ title, tone, items, titleKey }: { title: string; tone: Tone; items?: AiReportItem[]; titleKey?: 'title' | 'label' | 'name' | 'question' }) {
  if (!items?.length) return null;
  return (
    <Section title={title} tone={tone}>
      <div className="ai-items">
        {items.map((it, i) => <ItemRow key={i} item={it} titleKey={titleKey} tone={tone} />)}
      </div>
    </Section>
  );
}

/** רשימת פעולות ממוספרת (01/02/03) — רשימת UI ולא מסמך. */
function ActionList({ items, tone = 'blue' }: { items: AiReportItem[]; tone?: Tone }) {
  return (
    <div className="ai-actions">
      {items.map((it, i) => {
        const desc = it.details ?? it.insight;
        return (
          <div key={i} className="ai-action">
            <span className="ai-action-num" dir="ltr">{String(i + 1).padStart(2, '0')}</span>
            <div style={{ minWidth: 0 }}>
              <div className="ai-item-head">
                <div className="ai-item-title">{it.area ? `${it.area} — ` : ''}{it.title ?? it.label ?? ''}</div>
                {it.pct != null && it.pct > 0 && <span className={`ai-pct ai-pct--${tone}`}>{it.pct}% מהשיחות</span>}
              </div>
              {desc && <div className="ai-item-desc">{desc}</div>}
              <PhoneRefs refs={it.call_refs} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── דוח כלל עסקי ────────────────────────────────────────────────────

export function BusinessReportView({ report }: { report: AiBusinessReport }) {
  return (
    <div className="ai-stack">
      {report.exec_summary && (
        <Section title="תקציר מנהלים" tone="blue" variant="summary"><p className="ai-prose" style={{ margin: 0 }}>{report.exec_summary}</p></Section>
      )}
      {!!report.management_recommendations?.length && (
        <Section title="המלצות הנהלה" tone="green">
          <ActionList items={report.management_recommendations} tone="green" />
        </Section>
      )}
      <div className="ai-grid">
        <ItemsSection title="טרנדים כלליים" tone="orange" items={report.trends} />
        <ItemsSection title="מתחרים שעולים" tone="orange" items={report.competitors} titleKey="name" />
        <ItemsSection title="התנגדויות חוזרות" tone="red" items={report.objections} titleKey="label" />
        <ItemsSection title="השוואה בין נציגים" tone="purple" items={report.agent_comparison} titleKey="name" />
        <ItemsSection title="השוואה בין קמפיינים" tone="blue" items={report.campaign_comparison} titleKey="name" />
      </div>
    </div>
  );
}

// ── דוחות פר-נציג (נציגים / קמפיינים) עם סרגל צד ────────────────────

export function AgentsSidebar({ agents, activeId, onPick }: { agents: AiAgentEntry[]; activeId: number | null; onPick: (id: number) => void }) {
  return (
    <nav className="ai-agentnav" aria-label="נציגים">
      <div className="ai-agentnav-title">נציגים</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {agents.map((a) => (
          <button
            key={a.agent_id}
            type="button"
            aria-current={activeId === a.agent_id ? 'true' : undefined}
            onClick={() => onPick(a.agent_id)}
          >
            <span>{a.agent_name}</span>
            <span className="ai-count" dir="ltr">{a.sessions}</span>
          </button>
        ))}
      </div>
    </nav>
  );
}

/** דונאט קומפקטי עם צבע סמנטי לכל פרוסה (ה-Chart הכללי צובע לפי אינדקס). */
function Donut({ slices, size = 120 }: { slices: { label: string; value: number; color: string }[]; size?: number }) {
  const total = slices.reduce((s, x) => s + x.value, 0);
  const cx = size / 2;
  const r = size / 2 - 4;
  const stroke = 12;
  const c = 2 * Math.PI * (r - stroke / 2);
  let offset = 0;
  return (
    <svg width={size} height={size} role="img" aria-label="סנטימנט">
      <circle cx={cx} cy={cx} r={r - stroke / 2} fill="none" stroke="var(--ai-surface-soft)" strokeWidth={stroke} />
      {slices.map((s, i) => {
        const frac = total > 0 ? s.value / total : 0;
        const dash = c * frac;
        const el = (
          <circle
            key={i}
            cx={cx} cy={cx} r={r - stroke / 2}
            fill="none" stroke={s.color} strokeWidth={stroke}
            strokeDasharray={`${dash} ${c - dash}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${cx} ${cx})`}
          />
        );
        offset += dash;
        return el;
      })}
      <text x={cx} y={cx + 9} fontSize={26} fontWeight={700} fill="var(--ai-text)" textAnchor="middle">{total}</text>
    </svg>
  );
}

/**
 * פאנל אנליטיקה קומפקטי: שיחות · מענה · ציון | סנטימנט + תוצאות שיחה.
 * אותם מדדים שנשלפים פר-נציג (sentiment/resolution/answered/score).
 */
function AgentMetricsViz({ m, teamAvgScore }: { m: AiAgentMetrics; teamAvgScore?: number | null }) {
  const sentiment = Object.entries(m.sentiment ?? {})
    .filter(([, v]) => v > 0)
    .sort(([a], [b]) => (SENTIMENT_ORDER.indexOf(a) - SENTIMENT_ORDER.indexOf(b)));
  const answeredPct = m.sessions > 0 ? Math.round((m.answered / m.sessions) * 100) : 0;
  const resolution = Object.entries(m.resolution ?? {}).filter(([, v]) => v > 0);
  const resolTotal = resolution.reduce((s, [, v]) => s + v, 0);
  const scoreColor = teamAvgScore != null && m.avg_score != null && m.avg_score >= teamAvgScore ? 'var(--ai-ok)' : 'var(--ai-warn)';

  return (
    <div className="card ai-card">
      <div className="ai-analytics">
        <div className="ai-metric">
          <span className="ai-metric-value" dir="ltr"><CountUp value={m.sessions} /></span>
          <span className="ai-metric-label">שיחות</span>
          <span className="ai-metric-sub" dir="ltr">{Math.round(m.talk_sec_total / 60)} דק׳ דיבור</span>
        </div>
        <div className="ai-metric">
          <div className="ai-ring">
            <Ring value={answeredPct} max={100} size={64} color="var(--ai-primary)" />
            <span dir="ltr">{answeredPct}%</span>
          </div>
          <span className="ai-metric-label">מענה</span>
          <span className="ai-metric-sub" dir="ltr">{m.answered}/{m.sessions}</span>
        </div>
        {m.avg_score != null && (
          <div className="ai-metric">
            <div className="ai-ring">
              <Ring value={m.avg_score} max={100} size={64} color={scoreColor} />
              <span dir="ltr">{m.avg_score}</span>
            </div>
            <span className="ai-metric-label">ציון</span>
            {teamAvgScore != null && <span className="ai-metric-sub">ממוצע צוות {teamAvgScore}</span>}
          </div>
        )}
        <div className="ai-stack" style={{ gap: 14 }}>
          {sentiment.length > 0 && (
            <div className="ai-sentiment">
              <Donut slices={sentiment.map(([k, v], i) => ({ label: sentimentLabel(k), value: v, color: sentimentColor(k, i) }))} />
              <div className="ai-legend">
                {sentiment.map(([k, v], i) => (
                  <span key={k}>
                    <i style={{ background: sentimentColor(k, i) }} />
                    {sentimentLabel(k)} <b dir="ltr">{v}</b>
                  </span>
                ))}
              </div>
            </div>
          )}
          {resolTotal > 0 && (
            <div className="ai-outcomes">
              <div className="ai-outcomes-title">תוצאות שיחה</div>
              {resolution.map(([k, v]) => (
                <div key={k} className="ai-outcome">
                  <span className="ai-outcome-label" dir="auto">{k}</span>
                  <div className="ai-outcome-bar"><div style={{ width: `${Math.round((v / resolTotal) * 100)}%` }} /></div>
                  <span className="ai-outcome-val" dir="ltr">{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AgentHeader({ agent, teamAvgScore }: { agent: AiAgentEntry; teamAvgScore?: number | null }) {
  const m = agent.metrics;
  const answeredPct = m && m.sessions > 0 ? Math.round((m.answered / m.sessions) * 100) : null;
  return (
    <>
      <header className="ai-agent-head">
        <div>
          <h2 className="ai-agent-name">{agent.agent_name}</h2>
          <div className="ai-agent-meta">
            <span>{agent.sessions} שיחות</span>
            {answeredPct != null && <><span className="ai-sep">·</span><span>{answeredPct}% מענה</span></>}
            {m?.avg_score != null && <><span className="ai-sep">·</span><span>ציון {m.avg_score}{teamAvgScore != null && ` (צוות ${teamAvgScore})`}</span></>}
            {m && <><span className="ai-sep">·</span><span>{Math.round(m.talk_sec_total / 60)} דק׳ דיבור</span></>}
          </div>
        </div>
      </header>
      {m && <AgentMetricsViz m={m} teamAvgScore={teamAvgScore} />}
    </>
  );
}

/**
 * "פרופיל מצטבר" — אופי הנציג שנבנה מדוח לדוח: rolling_summary + traits
 * כצ'יפים רכים (לא כפתורים), עם מונה הדוחות שממנו נבנה.
 * 07/09/2026 (דורון): סקציה מתקפלת, **סגורה כברירת מחדל** — היא הרקע
 * ההיסטורי של הנציג ולא הדוח של התקופה, ולכן לא צריכה לדחוף למטה את
 * התקציר ואת ה-Action Items.
 */
function AgentProfileSection({ agent }: { agent: AiAgentEntry }) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const p = agent.profile;
  if (!p || (p.reports_count ?? 0) < 1 || (!p.rolling_summary && !(p.traits?.length))) return null;
  return (
    <section className="card ai-card ai-card--collapsible">
      <button
        type="button"
        className="ai-disclosure"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="ai-dot ai-dot--purple" />
        <h3>פרופיל מצטבר</h3>
        <span className="ai-sec-note">נבנה מ-{p.reports_count} דוחות</span>
        <span className={'ai-chev' + (open ? ' is-open' : '')} aria-hidden="true"><Icon name="chevronD" size={16} /></span>
      </button>
      <div id={bodyId} hidden={!open} className="ai-disclosure-body">
        {p.rolling_summary && <p className="ai-prose" style={{ margin: 0, fontSize: 14 }}>{p.rolling_summary}</p>}
        {!!p.traits?.length && (
          <div className="ai-chips">
            {p.traits.map((t, i) => (
              <span key={i} className="ai-chip"><Icon name="ai" size={13} /> {t}</span>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ReportFailed({ text }: { text: string }) {
  return (
    <Section title="הדוח לא הופק" tone="red">
      <p className="ai-item-desc" style={{ margin: 0 }}>{text}</p>
    </Section>
  );
}

export function AgentsReportView({ agents, teamAvgScore }: { agents: AiAgentEntry[]; teamAvgScore?: number | null }) {
  return (
    <div className="ai-agents-main">
      {agents.map((a) => (
        <article key={a.agent_id} id={`agent-${a.agent_id}`} className="ai-agent">
          <AgentHeader agent={a} teamAvgScore={teamAvgScore} />
          <AgentProfileSection agent={a} />
          {a.error || !a.report ? (
            <ReportFailed text="הפקת ה-AI לנציג זה נכשלה — מוצגים נתונים מספריים בלבד." />
          ) : (
            <>
              {a.report.exec_summary && <Section title="תקציר מנהלים" tone="blue" variant="summary"><p className="ai-prose" style={{ margin: 0 }}>{a.report.exec_summary}</p></Section>}
              <ItemsSection title="לשמר" tone="green" items={a.report.strengths} />
              <ItemsSection title="לשפר" tone="orange" items={a.report.improvements} />
              {!!a.report.action_items?.length && (
                <Section title="Action Items" tone="blue">
                  <ActionList items={a.report.action_items} />
                </Section>
              )}
              {a.report.outcome && <Section title="תוצאה" tone="purple" variant="result"><p className="ai-prose" style={{ margin: 0 }}>{a.report.outcome}</p></Section>}
            </>
          )}
        </article>
      ))}
    </div>
  );
}

/**
 * פאנל מדדים חיים בראש כרטיס הקמפיינים — תמונת-מצב של "היום" (נטען בנפרד
 * מהדוח ההיסטורי דרך /admin/insights/campaign-metrics).
 */
function CampaignLiveMetricsPanel() {
  const [m, setM] = useState<CampaignLiveMetrics | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    void adminApi.campaignLiveMetrics()
      .then((d) => { if (active) setM(d); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, []);

  if (failed) return null;

  const fmt = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('he-IL'));
  const cells: { label: string; value: string; hint?: string }[] = [
    { label: 'נכנסו היום לקמפיין', value: m ? fmt(m.leads_today) : '…', hint: 'לידים שנטענו לקמפיינים היום' },
    { label: 'לידים פעילים', value: m ? fmt(m.active_leads) : '…' },
    { label: 'ממתינים להיום', value: m ? fmt(m.waiting_today) : '…', hint: 'שיחות חוזרות שתוזמנו להיום' },
    { label: 'יחס שיחות לנציג', value: m ? fmt(m.calls_per_agent) : '…', hint: m ? `${fmt(m.calls_today)} שיחות · ${fmt(m.active_agents)} נציגים` : undefined },
  ];

  return (
    <div className="ai-live">
      {cells.map((c) => (
        <div key={c.label} className="card ai-card">
          <div className="ai-metric">
            <span className="ai-metric-label">{c.label}</span>
            <span className="ai-metric-value" dir="ltr">{c.value}</span>
            {c.hint && <span className="ai-metric-sub">{c.hint}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

export function CampaignsReportView({ agents }: { agents: AiAgentEntry[] }) {
  return (
    <div className="ai-agents-main">
      <CampaignLiveMetricsPanel />
      {agents.map((a) => (
        <article key={a.agent_id} id={`agent-${a.agent_id}`} className="ai-agent">
          <AgentHeader agent={a} />
          {!!a.campaigns_worked?.length && (
            <div className="ai-tags">
              <span>קמפיינים שעבד:</span>
              {a.campaigns_worked.map((c) => (
                <span key={c.campaign_id} className="ai-tag">{c.name} <b dir="ltr">({c.calls})</b></span>
              ))}
            </div>
          )}
          {a.error || !a.report ? (
            <ReportFailed text="הפקת ה-AI לנציג זה נכשלה ביום זה." />
          ) : (
            <>
              {a.report.lead_quality?.summary && (
                <Section title="איכות הלידים" tone="blue" variant="summary">
                  <p className="ai-prose" style={{ margin: 0 }}>{a.report.lead_quality.summary}</p>
                  <PhoneRefs refs={a.report.lead_quality.call_refs} />
                </Section>
              )}
              <div className="ai-grid">
                <ItemsSection title="מתחרים שעולים" tone="orange" items={a.report.competitors} titleKey="name" />
                <ItemsSection title="התנגדויות חוזרות" tone="red" items={a.report.objections} titleKey="label" />
                <ItemsSection title="שאלות חוזרות על המוצר" tone="blue" items={a.report.product_questions} titleKey="question" />
                <ItemsSection title="סיבות עיקריות לאי-סגירה" tone="orange" items={a.report.non_close_reasons} titleKey="label" />
              </div>
              <ItemsSection title="המלצות לשיווק/מוצר" tone="green" items={a.report.recommendations} />
            </>
          )}
        </article>
      ))}
    </div>
  );
}

export function ReportStatusNote({ status, onGenerate, generating }: { status: string; onGenerate: () => void; generating: boolean }) {
  const label = status === 'no_data' ? 'אין שיחות בתקופה זו — אין ממה להפיק דוח.'
    : status === 'pending' || status === 'running' ? 'הדוח בהפקה… העמוד יתעדכן אוטומטית.'
    : status === 'failed' ? 'הפקת הדוח נכשלה.'
    : 'אין דוח לתקופה זו.';
  return (
    <div className={'card ai-card ai-status' + (status === 'failed' ? ' ai-status--danger' : '')}>
      <div className="ai-status-icon"><Icon name="ai" size={22} /></div>
      <p>{label}</p>
      {(status === 'missing' || status === 'failed') && (
        <button type="button" className="btn primary" onClick={onGenerate} disabled={generating}>
          {generating ? 'מפיק…' : 'הפק דוח עכשיו'}
        </button>
      )}
    </div>
  );
}
